/**
 * Custom Shopify component for managing a multi-step starter kit flow.
 * Handles step transitions, variant/frequency selection, upsells,
 * quick buy logic, URL state syncing, and cart integration.
 */
if (!customElements.get('starter-kit')) {
  customElements.define(
    'starter-kit',
    class StarterKit extends HTMLElement {
      constructor() {
        super();

        this.boundInitHandlers = new WeakMap();
        this.boundSlideChangeHandlers = new WeakMap();
        this.initialStep = this.getAttribute('initial-step');
        this.hasOffers = this.getAttribute('has-offers') === 'true';
        this.hasManualAddToCart = this.getAttribute('has-manual-add-to-cart') === 'true';
        this.sectionId = this.getAttribute('section-id');
        this.layout = this.getAttribute('layout');
        this.strategy = this.getAttribute('strategy');
        this.frequencyStepType = this.getAttribute('frequency-step-type');
        this.hasOnetimeOption = this.getAttribute('has-onetime-option') === 'true';
        this.discountOfferLogic = this.getAttribute('discount-offer-logic');

        this.steps = this.initializeSteps();
        this.currentStep = this.steps[0];
        
        this.init();
      }

      /**
       * Initializes the steps based on the component's attributes.
       * @returns {Array<Object>} An array of step objects.
       */
      initializeSteps() {
        const isDiscountOffer = this.strategy === 'discount-offer';
        const steps = [];
      
        // Determine the first step
        if (this.frequencyStepType === 'first-step-merge') {
          steps.push({
            name: 'variant',
            nextStep: this.hasOffers || this.strategy === 'upsell-options' ? (isDiscountOffer ? 'offers' : 'upsell') : 'checkout',
            number: 1
          });
    
          if (this.hasOffers || this.strategy === 'upsell-options') {
            steps.push({
              name: isDiscountOffer ? 'offers' : 'upsell',
              nextStep: 'checkout',
              number: 2
            });
          }
        } else {
          if (this.initialStep === 'variant') {
            steps.push({ name: 'variant', nextStep: this.layout == 'stacking-steps' ? 'checkout' : 'frequency', number: 1 });
            
            if (this.layout != 'stacking-steps') {
              steps.push({ name: 'frequency', nextStep: this.hasOffers || this.strategy === 'upsell-options' ? (isDiscountOffer ? 'offers' : 'upsell') : 'checkout', number: 2 });
            }

            if (this.hasOffers || this.strategy === 'upsell-options') {
              steps.push({ name: isDiscountOffer ? 'offers' : 'upsell', nextStep: 'checkout', number: 3 });
            }
          } else {
            steps.push({ name: 'frequency', nextStep: this.hasOffers ? (isDiscountOffer ? 'offers' : 'upsell') : 'checkout', number: 1 });
            if (this.hasOffers) {
              steps.push({ name: isDiscountOffer ? 'offers' : 'upsell', nextStep: 'checkout', number: 2 });
            }
          }
        }
      
        // Add the checkout step
        steps.push({ name: 'checkout', number: steps.length + 1 });
      
        return steps;
      }

      /**
       * Initializes the component.
       */
      init() {
        this.initElements();
        this.handleInitialState();
        this.bindEventListeners();

        if (this.layout == 'step-by-step') {
          document.documentElement.style.scrollBehavior = 'initial';
        }

        document.addEventListener('starter-kit:update-content', this.handlePostUpdateContent.bind(this));
      }

      /**
       * Initializes the elements used in the component.
       */
      initElements () {
        this.elements = {
          slider: this.querySelectorAll('advanced-slider'),
          radioCards: this.querySelectorAll('.radio-card'),
          mainArea: this.querySelector('.starter-kit__main-area'),
          nextStepButton: this.querySelector('[data-next-step-button]'),
          progressBar: this.querySelector('[data-progress-bar]'),
          progressBarButtons: this.querySelectorAll('[data-progress-bar] button'),
          quickBuyButtons: this.querySelectorAll('quick-buy button.quick-buy__link'),
          totalPriceValue: this.querySelector('[data-total-price-value]'),
          footer: this.querySelector('[data-context="footer"]'),
          frequencySelect: this.frequencyStepType === 'first-step-merge' 
          ? Array.from(this.querySelectorAll('[data-context="variant"] .radio-card.active .frequency-select'))[0] 
          : this.querySelector('[data-context="frequency"] #frequency-select'),
          starterKitJson: JSON.parse(this.querySelector('[data-context="starter-kit-json"]').textContent),
        };

        this.querySelectorAll('details').forEach(el => {
          new Accordion(el);
        });
        
        this.querySelectorAll('[data-custom-select]').forEach(select => {
          Woolman.CustomSelect.initSingleSelect(select);
        });
      }

      /**
       * Binds event listeners to the component's elements.
       */
      bindEventListeners() {
        this.handleRadioChangeBound = this.handleRadioChange.bind(this);
        this.handleNextStepBound = this.handleNextStep.bind(this);
        this.handleProgressBarBound = this.handleProgressBar.bind(this);
        this.handleSlideChangeBound = debounce(this.handleSlideChange.bind(this), 300);
        this.handleFrequencySelectChangeBound = this.handleFrequencySelect.bind(this);
        this.handleSwatchClickBound = debounce(this.handleSwatchClick.bind(this), 300);

        if (this.elements.slider) {
          this.elements.slider.forEach(slider => {
            if (slider.swiper) {
              this.handleAdvancedSliderInit(slider);
            } else {
              slider.addEventListener('advanced-slider:init', () => this.handleAdvancedSliderInit(slider));
            }
          });
        }
      
        this.elements.radioCards.forEach(card => {
          const input = card.querySelector('input');
          if (input) {
            input.removeEventListener('change', this.handleRadioChangeBound);
            input.addEventListener('change', this.handleRadioChangeBound);

            if (card.classList.contains('active')) {
              if (this.currentStep.name === 'variant' && this.frequencyStepType === 'first-step-merge') {
                this.elements.frequencySelect = card.querySelector('.frequency-select');
                this.handleFrequencySelectChangeBound = this.handleFrequencySelect.bind(this);
        
                if (this.elements.frequencySelect) {
                  this.handleFrequencySelect({ currentTarget: this.elements.frequencySelect });
                }
              }
            }
          }
        });
      
        this.elements.progressBarButtons.forEach(button => {
          button.removeEventListener('click', this.handleProgressBarBound);
          button.addEventListener('click', this.handleProgressBarBound);
        });
      
        if (this.elements.nextStepButton) {
          this.elements.nextStepButton.removeEventListener('click', this.handleNextStepBound);
          this.elements.nextStepButton.addEventListener('click', this.handleNextStepBound);
        }

        if (this.elements.frequencySelect) {
          this.elements.frequencySelect.removeEventListener('change', this.handleFrequencySelectChangeBound);
          this.elements.frequencySelect.addEventListener('change', this.handleFrequencySelectChangeBound);
        }
      }

      /**
       * Unbinds event listeners from the component's elements.
       */
      unbindEventListeners() {
        if (this.elements.slider) {
          this.elements.slider.forEach(slider => {
            const boundInit = this.boundInitHandlers.get(slider);
            const boundSlideChange = this.boundSlideChangeHandlers.get(slider);
            if (boundInit) slider.removeEventListener('advanced-slider:init', boundInit);
            if (boundSlideChange) slider.removeEventListener('advanced-slider:slide-change', boundSlideChange);
          });
        }
      
        this.elements.radioCards.forEach(card => {
          const input = card.querySelector('input');
          if (input) {
            input.removeEventListener('change', this.handleRadioChangeBound);
          }
        });
      
        this.elements.progressBarButtons.forEach(button => {
          button.removeEventListener('click', this.handleProgressBarBound);
        });
      
        if (this.elements.nextStepButton) {
          this.elements.nextStepButton.removeEventListener('click', this.handleNextStepBound);
        }

        if (this.elements.frequencySelect) {
          this.elements.frequencySelect.removeEventListener('change', this.handleFrequencySelectChangeBound);
        }
      }

      /**
       * Handles the initialization of the advanced slider.
       */
      handleAdvancedSliderInit() {
        this.elements.slider.forEach(slider => {
          if (slider.swiper) {
            slider.addEventListener('advanced-slider:slide-change', (event) => this.handleSlideChangeBound(event.currentTarget));
          }
        });
      }

      /**
       * Initializes the quick buy buttons.
       */
      initQuickBuyButtons() {
        this.elements.quickBuyButtons.forEach(button => {
          const closestContext = button.closest('[data-context]').getAttribute('data-context');
          const quickBuyComponent = button.closest('quick-buy');
          const productCard = button.closest('product-card');
          const variantInput = quickBuyComponent.querySelector('input[name="items[0]id"]');
          const variantId = variantInput ? variantInput.value : null;
          const handle = productCard.getAttribute('handle');

          if (variantId) {
            let paramName;

            if (this.hasOffers) {
              paramName = 'offers';
            } else {
               // Determine which URL parameter to use based on the card layout
              const isUpgrade = closestContext == 'upgrade';
              paramName = isUpgrade ? 'upgrades' : 'upsell_products';
            }

            const paramValue = this.getUrlParams(paramName);
            const paramArray = paramValue ? paramValue.split(',') : [];

            if (this.hasManualAddToCart && this.hasOffers) {
              // Update all quick buy buttons to reflect the current state
              this.elements.quickBuyButtons.forEach(btn => {
                const handle = btn.closest('product-card').getAttribute('handle');
                const btnIsAdded = paramArray.some(param => param.startsWith(`${handle}:`));
                this.toggleQuickBuyButtonState(btn, btnIsAdded);
              });
            } else {
              const isAdded = paramArray.includes(`${handle}:${variantId}:subscription`) || paramArray.includes(`${handle}:${variantId}:onetime`);
              if (isAdded) this.toggleQuickBuyButtonState(button, isAdded);
            }

            button.addEventListener('click', (event) => {
              event.preventDefault();

              const quickBuyComponent = event.currentTarget.closest('quick-buy');
              const productCard = quickBuyComponent.closest('product-card');
              const variantInput = quickBuyComponent.querySelector('input[name="items[0]id"]');
              const variantId = variantInput ? variantInput.value : null;
              const handle = productCard.getAttribute('handle');
              let sellingPlan = null;

              this.elements.starterKitJson.extra_products.forEach(product => {
                if (product.variants) {
                  product.variants.forEach(variant => {
                    if (variant.id === parseInt(variantId)) {
                      sellingPlan = variant.selling_plans.find(plan => plan.id);
                    }
                  });
                }
              });

              // User has the option to add the product to the cart with or without a subscription
              if (sellingPlan) {
                // Check if the upsell product is already in the URL
                const upsellProducts = this.getUrlParams('upsell_products');
                const upsellArray = upsellProducts ? upsellProducts.split(',') : [];
              
                if (upsellArray.includes(`${handle}:${variantId}:subscription`) || upsellArray.includes(`${handle}:${variantId}:onetime`)) {
                  this.handleQuickBuy(handle, button, variantId, paramName, upsellArray.includes(`${handle}:${variantId}:subscription`));
                } else {
                  this.showQuickBuyModal(button, handle, variantId);
                }
              } else {
                this.handleQuickBuy(handle, button, variantId, paramName, false);
              }
            });
          }
        });
      }

      /**
       * Reinitializes the component.
       */
      reinitialize() {
        this.initElements();
        this.unbindEventListeners();
        this.bindEventListeners();
        this.initSwatches();
        this.initQuickBuyButtons();
        this.calculateCurrentPrice();
        this.moveFooterAfterCurrentStep();
      }

      /**
       * Handles the initial state of the component.
       */
      handleInitialState() {
        let stepName = this.getUrlParams('step');
        const hasVariantParam = Boolean(this.getUrlParams('variant'));

        if (!stepName) {
          this.updateUrl(this.initialStep, 'step', 'add');
          stepName = this.initialStep;
        }

        if (stepName == this.steps[0].name && !hasVariantParam) {
          this.prepareUrlForPredefinedProducts();
        }

        if (stepName === 'frequency') {
          this.prepareUrlFrequencyParam();
        }

        if (this.layout == 'stacking-steps') {
          const offerStep = this.steps.find(step => step.name === 'offers');

          if (!hasVariantParam) {
            this.prepareUrlForPredefinedProducts();
          }
          this.prepareUrlFrequencyParam();

          // Force update frequency step to show the subscription price. Subscription price is linked to the related subscription product metafield, but on the initial load, the URL param with the handle is not set.
          this.updateContent('frequency');

          // Force update offers step to update the actual variant id in the url.
          this.updateContent('offers');

          // If not manual add to cart, I want the offer to be added in the URL automatically
          if (!this.hasManualAddToCart) {
            this.prepareUrlForPredefinedProducts(null, null, 'offers', 'offers');
          }

          if (offerStep) {
            this.currentStep = offerStep;
          }

        } else {
          this.currentStep = this.steps.find(step => step.name === stepName);
        }

        this.initSwatches();
        this.initQuickBuyButtons();
        this.calculateCurrentPrice();
        this.moveFooterAfterCurrentStep();

        if (this.discountOfferLogic == 'deo-sampler-promo') {
          this.handleDeoSamplerPromo();
        }
      }

      /**
       * Handles the progress bar click event.
       * @param {Event} event - The event object.
       */
      handleProgressBar(event) {
        const currentTarget = event.currentTarget;
        const stepName = currentTarget.getAttribute('data-step');

        this.goToStep(stepName);
      }

      /**
       * Navigates to a specified step.
       * @param {string} stepName - The name of the step to navigate to.
       * @returns {Promise<void>}
       */
      async goToStep(stepName) {
        if (stepName === this.steps[0].name) {
          this.updateUrl('', 'frequency', 'remove');
          this.updateUrl('', 'upgrades', 'remove');
          this.updateUrl('', 'upsell_products', 'remove');
          this.updateUrl('', 'offers', 'remove');
        }

        this.updateUrl(stepName, 'step', 'update');
        await this.updateContent(stepName);

        this.currentStep = this.steps.find(step => step.name === stepName);

        this.scrollToStep();
      }

      /**
       * Handles the next step button click event.
       * @returns {Promise<void>}
       */
      async handleNextStep() {
        if (!this.currentStep.nextStep) return;
      
        if (this.currentStep.nextStep == 'checkout') {
          try {
            await this.addProductsToCart();
            window.location.href = `${window.Shopify.routes.root}checkout`;
            return;
          } catch (error) {
            console.error("Error while adding products to cart:", error);
            return;
          }
        }
      
        const nextContext = this.elements.nextStepButton.getAttribute('data-next-context');
      
        this.updateUrl(this.currentStep.nextStep, 'step', 'update');
        
        await this.updateContent(nextContext);
      
        this.currentStep = this.steps.find(step => step.name === this.currentStep.nextStep);
      
        this.scrollToStep();
      }

      /**
       * Handles the radio change event.
       * @param {Event} event - The event object.
       */
      handleRadioChange(event) {
        const currentTarget = event.currentTarget;
        const currentCard = currentTarget.closest('.radio-card');
        const nextContext = currentTarget.getAttribute('data-next-context');
        const urlParamName = currentTarget.getAttribute('data-url-param-name');
        const currentStep = currentTarget.getAttribute('data-current-step');

        if (currentStep == 'version') {
          const handle = currentTarget.getAttribute('data-handle');
          const variantId = currentTarget.value;
          
          this.updateCardStyles(currentCard);
          this.prepareUrlForPredefinedProducts(handle, variantId);
        } else if (currentStep == 'frequency' && this.hasOnetimeOption) {
          this.updateCardStyles(currentCard);
          this.handleFrequencyWithOnetime(currentTarget);
        } else {
          this.updateUrl(currentTarget.value, urlParamName, 'add');

          // I don't want to update the step if the layout is 'stacking-steps' because if the offers step is visible and the user changes the frequency, the footer button will handle the next step as 'offers'
          if (this.layout !== 'stacking-steps') {
            this.updateUrl(currentStep, 'step', 'update');
            this.currentStep = this.steps.find(step => step.name === currentStep);
          }

          this.prepareUrlForPredefinedProducts(null, null, 'variant');

          if (nextContext) {
            this.updateContent(nextContext);
          }

          this.updateCardStyles(currentCard);

          if (currentStep == 'variant' && this.frequencyStepType == 'first-step-merge') {
            this.elements.frequencySelect = currentCard.querySelector('.frequency-select');
            this.handleFrequencySelectChangeBound = this.handleFrequencySelect.bind(this);
            
            if (this.elements.frequencySelect) {
              this.handleFrequencySelect({ currentTarget: this.elements.frequencySelect });
            }
          }
        }
      }

      /**
       * Handles the frequency select change event.
       * @param {Event} event - The event object.
       */
      handleFrequencySelect(event) {
        const selectedValue = event.currentTarget.value;
        const sellingPlanNameKey = handleize(selectedValue).replaceAll('-', '_');
        const subtitle = this.elements.starterKitJson.selling_plan_names[sellingPlanNameKey]?.subtitle || '';
        const subtitleElement = this.querySelector('[data-context="frequency"] [data-selling-plan-subtitle]');

        if (subtitleElement) {
          subtitleElement.textContent = subtitle;
        }

        this.updateUrl(selectedValue, 'frequency', 'update');
      }

      /**
       * Handles frequency selection with onetime option.
       * @param {HTMLElement} currentTarget - The current target element.
       */
      handleFrequencyWithOnetime(currentTarget) {      
        // Get the current variant parameter from the URL
        const currentUrl = new URL(window.location.href);
        const params = new URLSearchParams(currentUrl.search);
        const variantParam = params.get('variant');
        const variantArray = variantParam ? variantParam.split(',') : [];
        const selectedSelectValue = this.elements.frequencySelect.value;
        const sellingPlanNameKey = handleize(selectedSelectValue).replaceAll('-', '_');
        const subtitle = this.elements.starterKitJson.selling_plan_names[sellingPlanNameKey]?.subtitle || '';
        const subtitleElement = this.querySelector('[data-context="frequency"] [data-selling-plan-subtitle]');
        
        if (subtitleElement) {
          subtitleElement.textContent = subtitle;
        }
      
        if (currentTarget.value === 'onetime') {
          this.updateUrl(currentTarget.value, 'frequency', 'update');
        } else {
          this.updateUrl(this.elements.frequencySelect.value, 'frequency', 'update');
        }
      }

      /**
       * Updates the styles of the radio cards.
       * @param {HTMLElement} currentCard - The current card element.
       */
      updateCardStyles(currentCard) {
        // Get the name of the radio input in the current card
        const currentInput = currentCard.querySelector('input[type="radio"]');
        if (!currentInput) return;
      
        const currentName = currentInput.getAttribute('name');
      
        // Remove 'active' class from all radio cards with the same name
        this.elements.radioCards.forEach((card) => {
          const input = card.querySelector('input[type="radio"]');
          if (input && input.getAttribute('name') === currentName) {
            if (currentCard !== card) {
              input.checked = false;
            }

            card.classList.remove('active');
          }
        });
      
        // Add 'active' class to the current card
        currentCard.classList.add('active');
      }

      /**
       * Updates the URL with a specified parameter.
       * @param {string} value - The value to set.
       * @param {string} paramName - The name of the parameter.
       * @param {string} action - The action to perform ('add', 'update', 'remove').
       */
      updateUrl(value, paramName, action) {
        const url = new URL(window.location.href);
        const urlParams = url.searchParams;

        // Handle action to add, update, or delete URL parameter
        switch (action) {
          case 'add':
          case 'update':
            if (urlParams.get(paramName) !== value) {
              urlParams.set(paramName, value);
            }
            break;
          case 'remove':
            urlParams.delete(paramName);
            break;
          default:
            console.warn(`Unsupported action: ${action}`);
            return;
        }

        // Update the URL in the browser without reloading the page
        history.replaceState(null, '', url.toString());
      }

      /**
       * Re-initializes Yotpo widgets after content updates
       */
      reinitializeYotpoWidgets() {
        setTimeout(() => {
          const widgets = this.querySelectorAll('.yotpo-widget-instance');
          
          if (widgets.length === 0) {
            return;
          }

          widgets.forEach((widget) => {
            const isLoaded = widget.hasAttribute('data-yotpo-element-loaded');
            
            if (!isLoaded) {
              const productId = widget.getAttribute('data-yotpo-product-id');
              
              if (productId) {
                this.fetchAndRenderYotpoRating(widget, productId);
              }
            }
          });
        }, 100);
      }

      /**
       * Fetches rating data from Yotpo API and manually renders it
       * @param {HTMLElement} widget - The widget element
       * @param {string} productId - The product ID
       */
      async fetchAndRenderYotpoRating(widget, productId) {
        try {
          // Get Yotpo store ID dynamically
          const storeId = this.getYotpoStoreId();
          
          if (!storeId) {
            console.error('[StarterKit] Yotpo store ID not found');
            return;
          }
          
          const apiUrl = `https://api-cdn.yotpo.com/v3/storefront/store/${storeId}/product/${productId}/ratings`;
          const response = await fetch(apiUrl);
          
          if (!response.ok) {
            return;
          }
          
          const data = await response.json();
          
          // Check if bottomline exists and has the data (note: camelCase field names)
          if (data && data.bottomline && data.bottomline.averageScore && data.bottomline.totalReviews) {
            const averageScore = parseFloat(data.bottomline.averageScore).toFixed(1);
            const totalReviews = data.bottomline.totalReviews;
            
            // Create rating HTML matching Yotpo's exact structure
            const ratingHTML = `
              <div class="yotpo-reviews-star-ratings-widget yotpo-star-ratings-widget-override-css yotpo-device-laptop yotpo-display-m" style="display: flex; justify-content: left; flex-direction: row; margin-bottom: 5px;">
                <div class="yotpo-widget-clear yotpo-bottom-line-scroll-panel" style="display: flex; align-items: flex-start; flex-flow: column wrap; gap: 4px;">
                  <button class="yotpo-sr-bottom-line-summary yotpo-sr-bottom-line-button" type="button" aria-label="${averageScore} out 5 stars rating in total ${totalReviews} reviews" style="display: flex; flex-direction: row; align-items: center; gap: 3px; margin-right: 0px; direction: ltr; cursor: pointer; background: none; border: none; padding: 0;">
                    <span aria-hidden="true" style="display: flex; flex-direction: row; align-items: center; margin-left: 0px; height: 28px; cursor: pointer;">
                      ${this.generateStars(averageScore)}
                    </span>
                    <span class="yotpo-sr-bottom-line-left-panel yotpo-sr-bottom-line-score" style="display: flex; align-self: center; color: rgb(17, 17, 17); font-style: normal; font-weight: 700; font-size: 16px; margin-inline: 0px 12px; margin-bottom: 1px; padding-top: 2px;">${averageScore}</span>
                    <span class="yotpo-sr-bottom-line-right-panel" style="display: flex;">
                      <span class="yotpo-sr-bottom-line-text yotpo-sr-bottom-line-text--right-panel" style="white-space: nowrap; font-size: 16px; text-align: start; font-style: normal; font-weight: 400; color: rgb(17, 17, 17); padding-top: 2px; line-height: 28px;"> ${totalReviews} reviews </span>
                    </span>
                  </button>
                </div>
              </div>
            `;
            
            widget.innerHTML = ratingHTML;
            widget.setAttribute('data-yotpo-element-loaded', 'true');
          }
        } catch (error) {
          console.error('[StarterKit] Error fetching Yotpo rating:', error);
        }
      }

      /**
       * Generates star SVG HTML
       * @param {number} rating - The rating score
       * @returns {string} Stars HTML
       */
      generateStars(rating) {
        const stars = [];
        const fullStars = Math.floor(rating);
        const partialStar = rating - fullStars;
        
        for (let i = 0; i < 5; i++) {
          let fillPercentage = 0;
          
          if (i < fullStars) {
            // Full star
            fillPercentage = 100;
          } else if (i === fullStars && partialStar > 0) {
            // Partial star
            fillPercentage = partialStar * 100;
          }
          // else: empty star (fillPercentage = 0)
          
          const starSVG = `
            <svg aria-hidden="true" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" class="star-container yotpo-sr-star-${fillPercentage === 100 ? 'full' : fillPercentage > 0 ? 'partial' : 'empty'}" width="15" height="15" style="display: flex; flex-direction: row; margin-inline-end: ${i === 4 ? '8px' : '3.5px'};">
              <defs>
                <linearGradient id="yotpo_stars_gradient_${i}_${Date.now()}">
                  <stop offset="${fillPercentage}%" stop-color="#111111FF"></stop>
                  <stop stop-opacity="1" offset="${fillPercentage}%" stop-color="#FFFFFF"></stop>
                </linearGradient>
              </defs>
              <path style="pointer-events: none;" d="M9 14.118L14.562 17.475L13.086 11.148L18 6.891L11.529 6.342L9 0.375L6.471 6.342L0 6.891L4.914 11.148L3.438 17.475L9 14.118Z" stroke="#111111FF" fill="url('#yotpo_stars_gradient_${i}_${Date.now()}')"></path>
            </svg>
          `;
          stars.push(starSVG);
        }
        
        return stars.join('');
      }

      /**
       * Get Yotpo store ID dynamically from page
       * @returns {string|null} Store ID or null if not found
       */
      getYotpoStoreId() {
        // Try to find from Yotpo loader script
        const yotpoScripts = document.querySelectorAll('script[src*="yotpo"]');
        for (const script of yotpoScripts) {
          const srcMatch = script.src.match(/loader\/([A-Za-z0-9_-]+)/);
          if (srcMatch) {
            return srcMatch[1];
          }
        }
        
        return null;
      }

      /**
       * Updates the content of the component.
       * @param {string} context - The context to update.
       * @returns {Promise<void>}
       */
      async updateContent(context, specificContext = false) {
        try {
          // Disable next step button during loading
          if (this.elements.nextStepButton) {
            this.elements.nextStepButton.disabled = true;
            this.elements.nextStepButton.classList.add('loading');
            this.elements.nextStepButton.querySelector('.loading-overlay')?.classList.remove('hidden');
          }
      
          const sectionHtml = await this.fetchSectionHtml();
      
          if (sectionHtml) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(sectionHtml, 'text/html');
      
            // Update content for each context div
            const contextDivs = this.querySelectorAll('[data-context]');
            const updatePromises = Array.from(contextDivs).map(div => {
              return new Promise(resolve => {
                const contextName = div.getAttribute('data-context');
                
                // Check if we should update this specific context
                if (specificContext && contextName !== context) {
                  resolve();
                  return;
                }
      
                const newContentElement = doc.querySelector(`[data-context="${contextName}"]`);
                
                if (newContentElement && !this.elementsAreEqual(div, newContentElement)) {             
                  div.innerHTML = newContentElement.innerHTML;
                  div.className = newContentElement.className;
                }
                resolve();
              });
            });
      
            // Wait for all content updates to complete
            await Promise.all(updatePromises);
      
            // Dispatch update event
            document.dispatchEvent(new CustomEvent('starter-kit:update-content', { 
              detail: { context, success: true }
            }));

            // Re-initialize Yotpo widgets after content update
            this.reinitializeYotpoWidgets();
          }
        } catch (error) {
          console.error('Error updating content:', error);
          document.dispatchEvent(new CustomEvent('starter-kit:update-content', { 
            detail: { context, success: false, error }
          }));
        } finally {          
          if (this.elements.nextStepButton) {
            this.elements.nextStepButton.classList.remove('loading');
            this.elements.nextStepButton.querySelector('.loading-overlay')?.classList.add('hidden');
            
            // Only enable the button if we have a variant selected (for variant step)
            const variantParam = this.getUrlParams('variant');
            const isVariantStep = this.currentStep?.name === 'variant';
            
            this.elements.nextStepButton.disabled = isVariantStep && !variantParam;
          }
        }
      }

      /**
       * Fetches the HTML for a section.
       * @returns {Promise<string>} The HTML string for the section.
       */
      async fetchSectionHtml() {
        try {
          const url = new URL(window.location.href);
          const urlParams = new URLSearchParams(url.searchParams);
      
          // Set the section parameter to get the specific section's HTML
          urlParams.set('sections', this.sectionId);

          urlParams.set('view', Date.now());

          const fetchUrl = `${window.Shopify.routes.root}?${urlParams.toString()}`;
          
          const response = await fetch(fetchUrl);
          if (!response.ok) {
            throw new Error(`Failed to fetch section: ${response.statusText}`);
          }
      
          // Shopify sections API returns HTML directly in the response as an object
          const data = await response.json();
          return data[this.sectionId]; // Return HTML for the specific section ID
      
        } catch (error) {
          console.error("Error fetching section:", error);
        }
      }

      /**
       * Retrieves URL parameters.
       * @param {string|null} param - The parameter to retrieve.
       * @returns {string|URLSearchParams} The parameter value or all parameters.
       */
      getUrlParams(param = null) {
        const url = new URL(window.location.href);
        const urlParams = new URLSearchParams(url.searchParams);

        if (param) {
          return urlParams.get(param);
        }

        return urlParams;
      }

      /**
       * Handles the slide change event.
       * @param {HTMLElement} slider - The slider element.
       */
      handleSlideChange(slider) {
        const handle = slider.getAttribute('handle');
        const variantId = slider.getAttribute('variant-id');
        const urlParam = slider.getAttribute('url-param');
        const closestContext = slider.closest('[data-context]');

        const radioCard = this.querySelector('[data-context="variant"] .radio-card.active');
        if (radioCard) {
          const checkedRadio = radioCard.querySelector('input[type="radio"]:checked');
          checkedRadio.dataset.variantId = variantId;
        }
      
        if (handle && variantId && closestContext && !closestContext.classList.contains('disabled')) {
          this.prepareUrlForPredefinedProducts(handle, variantId, closestContext.getAttribute('data-context'), urlParam);

          if (this.discountOfferLogic == 'deo-sampler-promo') {
            this.handleDeoSamplerPromo();
          }
        }
      }

      /**
       * Initializes swatches.
       */
      initSwatches() {
        this.querySelectorAll('.swatch').forEach(swatch => {
          swatch.removeEventListener('click', this.handleSwatchClickBound);
          swatch.addEventListener('click', this.handleSwatchClickBound);
        });
      }

      /**
       * Handles swatch click events.
       * @param {Event} event - The event object.
       */
      handleSwatchClick(event) {
        const currentTarget = event.currentTarget || event.target;
        const currentParent = currentTarget.closest('product-card') || currentTarget.closest('advanced-slider');
      
        const handle = currentParent.getAttribute('handle');
        const variantId = currentTarget.getAttribute('data-variant-id') || currentParent.getAttribute('variant-id');
        const urlParam = currentParent.closest('[data-context]').getAttribute('data-context');

        if (handle && variantId) {
          if (urlParam === 'version') {
            this.prepareUrlForPredefinedProducts(handle, variantId);
          } else if (urlParam === 'offers') {
            if (!this.hasManualAddToCart) {
              this.prepareUrlForPredefinedProducts(handle, variantId, 'offers', urlParam);
            } else {
              const url = new URL(window.location);
              const offersParam = url.searchParams.get('offers');
              
              if (offersParam) {
                const offersEntries = offersParam.split(',');
                const offersHandles = offersEntries.map(entry => entry.split(':')[0]);
              
                if (offersHandles.includes(handle)) {
                  this.prepareUrlForPredefinedProducts(handle, variantId, 'offers', urlParam);
                }
              }
            }
          }

          if (this.currentStep.name === 'upsell') {
            const upsellProducts = this.getUrlParams('upsell_products');
            const upsellArray = upsellProducts ? upsellProducts.split(',') : [];
            
            const upgradeProducts = this.getUrlParams('upgrades');
            const upgradeArray = upgradeProducts ? upgradeProducts.split(',') : [];
            
            const quickBuyButton = currentParent.querySelector('quick-buy button.quick-buy__link');
            const isAdded = upsellArray.includes(`${handle}:${variantId}:subscription`) || upsellArray.includes(`${handle}:${variantId}:onetime`) ||
                            upgradeArray.includes(`${handle}:${variantId}:subscription`) || upgradeArray.includes(`${handle}:${variantId}:onetime`);
            this.toggleQuickBuyButtonState(quickBuyButton, isAdded);
          } else if (this.hasManualAddToCart && this.hasOffers) {
            const url = new URL(window.location);
            const params = url.searchParams;
            const paramArray = params.get('offers') ? params.get('offers').split(',') : [];

            this.elements.quickBuyButtons.forEach(btn => {
              const handle = btn.closest('product-card').getAttribute('handle');
              const btnIsAdded = paramArray.some(param => param.startsWith(`${handle}:`));
  
              this.toggleQuickBuyButtonState(btn, btnIsAdded);
            });
          }

          if (this.discountOfferLogic == 'deo-sampler-promo') {
            this.handleDeoSamplerPromo();
          }
        }
      }

      /**
       * Prepares the URL for predefined products.
       * @param {string|null} handleParam - The handle parameter.
       * @param {string|null} variantIdParam - The variant ID parameter.
       * @param {string} context - The context.
       * @param {string} urlParamName - The URL parameter name.
       */
      prepareUrlForPredefinedProducts(handleParam = null, variantIdParam = null, context = 'version', urlParamName = 'variant') {
        const variantQuery = [];
        const currentUrlParams = new URLSearchParams(window.location.search);
        let existingItems = [];
        for (const [key, value] of currentUrlParams.entries()) {
          const splitValues = value.split(',');
          const productPairs = splitValues.filter(item => item.includes(':'));
          existingItems = [...existingItems, ...productPairs];
        }
        
        // Check for advanced-slider and product-card elements
        const advancedSlider = this.querySelector(`[data-context="${context}"] advanced-slider`);
        const productCards = this.querySelectorAll(`[data-context="${context}"] product-card`);
        const radioCards = this.querySelectorAll(`[data-context="${context}"] .radio-card`);
      
        // Process the advanced-slider element if it exists
        if (advancedSlider) {
          const handle = handleParam || advancedSlider.getAttribute('handle');
          const variantId = variantIdParam || advancedSlider.getAttribute('variant-id');
          if (handle && variantId) {
            variantQuery.push(`${handle}:${variantId}`);
          }
        }
      
        if (this.hasOffers && this.hasManualAddToCart) {
          productCards.forEach((productCard) => {
            const handle = productCard.getAttribute('handle');
        
            if (handleParam) {
              if (handle === handleParam && variantIdParam) {
                variantQuery.push(`${handle}:${variantIdParam}`);
              }
            }
          });
        } else {
          // In case of BOTH option in the initial variant selector
          productCards.forEach((productCard) => {
            const handle = productCard.getAttribute('handle');
            const variantId = productCard.getAttribute('variant-id');

            if (handleParam) {
              if (handle === handleParam && variantIdParam) {
                variantQuery.push(`${handle}:${variantIdParam}`);
              }  else {
                variantQuery.push(`${handle}:${variantId}`);
              }
            } else if (handle && variantId) {
              // Otherwise, add all product-card elements
              variantQuery.push(`${handle}:${variantId}`);
            }
          });
        }
      
        // Process each radio-card element
        radioCards.forEach((radioCard) => {
          const checkedRadio = radioCard.querySelector('input[type="radio"]:checked');
          
          if (checkedRadio) {
            const handle = checkedRadio.getAttribute('data-handle');
            const variantId = checkedRadio.getAttribute('data-variant-id');
      
            // Only update the specified handle if handleParam is provided
            if (handleParam) {
              if (handle === handleParam && variantIdParam) {
                variantQuery.push(`${handle}:${variantIdParam}`);
              } else {
                variantQuery.push(`${handle}:${variantId}`);
              }
            } else if (handle && variantId) {
                const entry = `${handle}:${variantId}`;
                const idExist = existingItems.some(item => item.split(':')[1] === variantId);
                if (!idExist) {
                  variantQuery.push(entry);
                }
              }
          }
        });
      
        // Join variant entries with commas to form the final query string
        const finalQuery = variantQuery.join(',');
      
        // Update the URL if there is any variant information to add
        if (finalQuery) {
          this.updateUrl(finalQuery, urlParamName, 'add');
        }
      }

      /**
       * Prepares the URL frequency parameter.
       */
      prepareUrlFrequencyParam() {
        const frequencyParam = this.getUrlParams('frequency');
      
        if (!frequencyParam) {
          if (this.hasOnetimeOption) {
            // Get the selected option from the select element or default to the first one
            const frequencySelect = this.querySelector('[data-context="frequency"] select');
            if (frequencySelect) {
              const selectedOption = frequencySelect.value || frequencySelect.options[0].value;
              this.updateUrl(selectedOption, 'frequency', 'add');
            }
          } else {
            const frequencyOptions = this.querySelectorAll('[data-context="frequency"] input[type="radio"]');
            if (frequencyOptions.length > 0) {
              // Find the checked input
              const checkedFrequency = Array.from(frequencyOptions).find(option => option.checked);
              const frequencyValue = checkedFrequency ? checkedFrequency.value : frequencyOptions[0].value;
              this.updateUrl(frequencyValue, 'frequency', 'add');
            }
          }
        }
      }
      
      /**
       * Handles post-update content events.
       * @param {CustomEvent} event - The custom event object.
       */
      handlePostUpdateContent(event) {
        const { context } = event.detail;

        if (context === 'version') {
          this.prepareUrlForPredefinedProducts();

          if (this.discountOfferLogic == 'deo-sampler-promo') {
            this.handleDeoSamplerPromo();
          }

          // Preven scrolling with the frequency step is merged in the first step
          if (this.frequencyStepType != 'first-step-merge') {
            const versionElement = this.querySelector('[data-context="version"]');
            if (versionElement) {
              versionElement.classList.remove('disabled');
              this.scrollToStep('version');
            }
          }
        }

        if (context === 'frequency') {
          this.prepareUrlFrequencyParam();
        }

        if (context === 'offers' && !this.hasManualAddToCart) {
          setTimeout(() => {
            this.prepareUrlForPredefinedProducts(null, null, 'offers', 'offers');
          }, 300);
        }

        this.reinitialize();
      }

      /**
       * Compares two elements for equality.
       * @param {HTMLElement} oldEl - The old element.
       * @param {HTMLElement} newEl - The new element.
       * @returns {boolean} True if elements are equal, false otherwise.
       */
      elementsAreEqual(oldEl, newEl) {
        // Compare tag names
        if (oldEl.tagName !== newEl.tagName) return false;
      
        // Compare text content
        if (oldEl.textContent.trim() !== newEl.textContent.trim()) return false;
      
        // Compare classes
        const oldClasses = [...oldEl.classList].sort().join(' ');
        const newClasses = [...newEl.classList].sort().join(' ');
        if (oldClasses !== newClasses) return false;
      
        // Compare all attributes
        const oldAttributes = oldEl.attributes;
        const newAttributes = newEl.attributes;
        
        if (oldAttributes.length !== newAttributes.length) return false;
      
        for (let attr of oldAttributes) {
          if (attr.name !== 'class' && newEl.getAttribute(attr.name) !== attr.value) return false;
        }
      
        // Compare child nodes recursively
        const oldChildren = oldEl.children;
        const newChildren = newEl.children;
        
        if (oldChildren.length !== newChildren.length) return false;
      
        for (let i = 0; i < oldChildren.length; i++) {
          if (!this.elementsAreEqual(oldChildren[i], newChildren[i])) return false;
        }
      
        return true;
      }

      /**
       * Handles quick buy actions.
       * @param {string} handle - The product handle.
       * @param {HTMLElement} button - The button element.
       * @param {string} variantId - The variant ID.
       * @param {string} paramName - The parameter name.
       * @param {boolean} isSubscription - Whether it's a subscription.
       */
      handleQuickBuy(handle, button, variantId, paramName, isSubscription = false) {
        const url = new URL(window.location);
        const params = url.searchParams;
        let paramArray = params.get(paramName) ? params.get(paramName).split(',') : [];
        const variantEntry = `${handle}:${variantId}:${isSubscription ? 'subscription' : 'onetime'}`;

        const isAdded = paramArray.includes(variantEntry);

        if (this.hasManualAddToCart) {
          // If manual add to cart is enabled, replace the existing entry
          if (!isAdded && paramArray.length > 0) {
            // Remove the existing entry
            paramArray = [];
          }
        }

        if (isAdded) {
          paramArray = paramArray.filter(entry => entry !== variantEntry);
        } else {
          paramArray.push(variantEntry);
        }

        const finalParamValue = paramArray.join(',');
        this.updateUrl(finalParamValue, paramName, paramArray.length > 0 ? 'update' : 'remove');

        if (this.hasManualAddToCart) {
          // Update all quick buy buttons to reflect the current state
          this.elements.quickBuyButtons.forEach(btn => {
            const handle = btn.closest('product-card').getAttribute('handle');
            const btnIsAdded = paramArray.some(param => param.startsWith(`${handle}:`));

            this.toggleQuickBuyButtonState(btn, btnIsAdded);
          });
        } else {
          this.toggleQuickBuyButtonState(button, !isAdded);
        }

        this.calculateCurrentPrice();
      }

      /**
       * Shows the quick buy modal.
       * @param {HTMLElement} button - The button element.
       * @param {string} handle - The product handle.
       * @param {string} variantId - The variant ID.
       * @returns {Promise<void>}
       */
      async showQuickBuyModal(button, handle, variantId) {
        try {
          if (this.drawer) {
            this.drawer.remove();
          }

          const rootUrl = window.routes.root === '/' ? '/' : `${window.routes.root}/`;
          const response = await fetch(`${rootUrl}products/${handle}?view=ajax-starter-kit-quick-buy-drawer&variant=${variantId}`);
          
          if (!response.ok) {
            throw new Error(`Failed to fetch modal content: ${response.statusText}`);
          }

          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const drawerElement = doc.body.firstChild;

          document.body.appendChild(drawerElement);
          this.drawer = drawerElement;

          // Bind event listeners
          this.drawer.querySelector('[data-close]').addEventListener('click', Woolman.ModalsAndDrawers.closeModalOrDrawerOrDrawerFromEvent);
          this.drawer.querySelector('[data-overlay]').addEventListener('click', Woolman.ModalsAndDrawers.closeModalOrDrawerOrDrawerFromEvent);

          this.drawer.querySelector('[data-add-as-subscription]').addEventListener('click', (event) => {
            this.handleQuickBuy(handle, button, variantId, 'upsell_products', true);
            Woolman.ModalsAndDrawers.closeModalOrDrawerOrDrawerFromEvent(event);
          });
          
          this.drawer.querySelector('[data-add-as-onetime]').addEventListener('click', (event) => {
            this.handleQuickBuy(handle, button, variantId, 'upsell_products', false);
            Woolman.ModalsAndDrawers.closeModalOrDrawerOrDrawerFromEvent(event);
          });

          Woolman.ModalsAndDrawers.showModalOrDrawer('starter-kit-quick-buy-drawer');
        } catch (error) {
          console.error("Error showing quick buy modal:", error);
        }
      }

      /**
       * Toggles the state of a quick buy button.
       * @param {HTMLElement} button - The button element.
       * @param {boolean} isAdded - Whether the product is added.
       */
      toggleQuickBuyButtonState(button, isAdded) {
        const parentCard = button.closest('product-card');
        const textElement = button.querySelector('.quick-buy__text');

        if (isAdded) {
          textElement.textContent = window.variantStrings.added;

          setTimeout(() => {
            if (parentCard.classList.contains('layout--highlight')) {
              button.classList.replace('btn--primary', 'btn--secondary');
            } else {
              button.classList.replace('btn--secondary', 'btn--primary');
            }
            textElement.textContent = window.variantStrings.quickBuyLabelRemove;
          }, 2500);
        } else {
          if (parentCard.classList.contains('layout--highlight')) {
            button.classList.replace('btn--secondary', 'btn--primary');
          } else {
            button.classList.replace('btn--primary', 'btn--secondary');
          }

          textElement.textContent = textElement.getAttribute('data-initial-text');
        }
      }

      async addProductsToCart() {
        try {
          const url = new URL(window.location);
          const params = url.searchParams;
      
          const frequencyParam = params.get('frequency');
          const variantParam = params.get('variant');
          const variantArray = variantParam ? variantParam.split(',').map(item => item.split(':')[1]) : [];
      
          const upgradesParam = params.get('upgrades');
          const upgradesArray = upgradesParam ? upgradesParam.split(',').map(item => item.split(':')[1]) : [];
      
          const upsellProducts = params.get('upsell_products');
          const upsellParamArray = upsellProducts ? upsellProducts.split(',') : [];
          const upsellArray = upsellParamArray.map(item => item.split(':')[1]);
      
          const offersParam = params.get('offers');
          const offersArray = offersParam ? offersParam.split(',').map(item => item.split(':')[1]) : [];
      
          const allVariantIds = [...(upgradesArray.length > 0 ? [] : variantArray), ...upsellArray, ...upgradesArray, ...offersArray];
      
          if (allVariantIds.length === 0) {
            console.log("No products to add to the cart.");
            return;
          }
      
          const findSellingPlanAndTitle = (productList, variantId, isVariantParam, isUpgradeParam, isSubscription) => {
            let sellingPlan = null;
            let sellingPlanName = null;
            let relatedSubscriptionTitle = null;
      
            for (const product of productList) {
              for (const variant of product.variants) {
                if (variant.id === parseInt(variantId, 10)) {
                  if (isVariantParam || isUpgradeParam || isSubscription) {
                    const frequencyParamLower = handleize(frequencyParam);

                    sellingPlan = variant.selling_plans.find(plan => {
                      // Prefer the default name if available, otherwise use the regular name
                      const planName = plan.name_default || plan.name;
                      let planNameHandleized = handleize(planName);
                    
                      // First, try direct match
                      if (frequencyParamLower === planNameHandleized) {
                        return true;
                      }

                      /*
                    
                      // TEMP FIX: Map legacy handleized names to new ones
                      const tempFixMap = {
                        'every-1-month': 'delivery-every-month',
                        'delivery-every-month': 'every-1-month',
                        'every-2-months': 'delivery-every-2-months',
                        'delivery-every-second-month': 'every-2-months',
                        'every-3-months': 'delivery-every-3-months',
                        'delivery-every-third-month': 'every-3-months',
                        'every-4-months': 'delivery-every-4-months',
                        'delivery-every-fourth-month': 'every-4-months'
                      };
                    
                      if (tempFixMap[planNameHandleized]) {
                        planNameHandleized = tempFixMap[planNameHandleized];
                        if (frequencyParamLower === planNameHandleized) {
                          return true;
                        }
                      }

                      */
                    
                      return false;
                    });
      
                    if (sellingPlan) {
                      sellingPlanName = sellingPlan.name;
                    }
      
                    if (product.related_subscription_product) {
                      relatedSubscriptionTitle = product.related_subscription_product.title;
                    }
      
                    return { sellingPlan, sellingPlanName, relatedSubscriptionTitle }; // Return early when found
                  }
                }
              }
            }
      
            return { sellingPlan, sellingPlanName, relatedSubscriptionTitle };
          };
      
          const items = allVariantIds.map(variantId => {
            let sellingPlan = null;
            let relatedSubscriptionTitle = null;
            let sellingPlanName = null;
      
            const isVariantParam = variantArray.includes(variantId);
            const isUpgradeParam = upgradesArray.includes(variantId);
            const upsellEntry = upsellParamArray.find(entry => entry.startsWith(variantId));
            const isSubscription = upsellEntry ? upsellEntry.endsWith(':subscription') : false;
      
            const findMatchingVariant = (sourceVariants, targetVariants) => {
              const sourceVariant = sourceVariants.find(variant => variant.id === parseInt(variantId, 10));

              if (sourceVariant) {
                const matchingTargetVariant = targetVariants.find(targetVariant => targetVariant.title === sourceVariant.title);
                if (matchingTargetVariant) {
                  return matchingTargetVariant.id;
                }
              }

              return variantId; // Return original if no match found
            };
      
            if (frequencyParam === 'onetime') {
              if (isUpgradeParam) {
                this.elements.starterKitJson.upgrade_products.forEach(product => {
                  variantId = findMatchingVariant(product.variants, product.onetime_product.variants);
                });
              } else {
                this.elements.starterKitJson.product_options.forEach(product => {
                  variantId = findMatchingVariant(product.variants, product.onetime_product.variants);
                });
              }

            } else {
              if (isUpgradeParam) {
                ({ sellingPlan, sellingPlanName, relatedSubscriptionTitle } = findSellingPlanAndTitle(this.elements.starterKitJson.upgrade_products, variantId, isVariantParam, isUpgradeParam, isSubscription));
              } else {
                ({ sellingPlan, sellingPlanName, relatedSubscriptionTitle } = findSellingPlanAndTitle(this.elements.starterKitJson.product_options, variantId, isVariantParam, isUpgradeParam, isSubscription));
              }
            }
      
            const properties = {};
      
            if (isVariantParam || isUpgradeParam || isSubscription) {
              if (relatedSubscriptionTitle) {
                properties._related_subscription_product_title = relatedSubscriptionTitle;
              }
              if (sellingPlanName) {
                properties._related_subscription_product_selling_plan_name = sellingPlanName;
              }
            }
      
            return {
              id: variantId,
              quantity: 1,
              ...(sellingPlan && { selling_plan: sellingPlan.id }),
              ...(Object.keys(properties).length && { properties })
            };
          });
      
          const options = {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ items }),
          };
      
          const response = await fetch(routes.cart_add_url + '.js', options);
          const responseData = await response.json();
      
          if (response.ok) {
            console.log("All products added to cart successfully");
            return responseData;
          } else {
            console.error("Error adding products to cart:", response.statusText);
            throw new Error(responseData);
          }
        } catch (error) {
          console.error("Error adding products to cart:", error);
          throw error;
        }
      }
      
      calculateCurrentPrice() {
        if (!this.elements.totalPriceValue) return;

        const url = new URL(window.location);
        const params = url.searchParams;
      
        // Extract variant, upgrade, and upsell IDs from URL
        const variantArray = params.get('variant') ? params.get('variant').split(',').map(item => item.split(':')[1]) : [];
        const upgradesArray = params.get('upgrades') ? params.get('upgrades').split(',').map(item => item.split(':')[1]) : [];
        const upsellArray = params.get('upsell_products') ? params.get('upsell_products').split(',').map(item => item.split(':')[1]) : [];
      
        let totalPrice = 0;
        const processedVariants = new Set();  // Track processed variant IDs
      
        // Helper function to find price by variant ID in a specific product list, ensuring no duplicate sums
        const findPriceInProductList = (productList, variantIds) => {
          productList.forEach(product => {
            product.variants.forEach(variant => {
              if (variantIds.includes(String(variant.id)) && !processedVariants.has(variant.id)) {
                totalPrice += variant.price;
                processedVariants.add(variant.id);  // Mark this variant ID as processed
              }
            });
          });
        };
      
        // Calculate total price for each section
        if (upgradesArray.length == 0) {
          findPriceInProductList(this.elements.starterKitJson.product_options, variantArray);
        }

        findPriceInProductList(this.elements.starterKitJson.upgrade_products, upgradesArray);
        findPriceInProductList(this.elements.starterKitJson.extra_products, upsellArray);
      
        // Update the displayed total price
        this.elements.totalPriceValue.innerHTML = Woolman.Utils.formatMoney(totalPrice, null, true);
      } 

      /**
       * Scrolls to the current step.
       * @param {string|null} context - The context to scroll to.
       */
      scrollToStep(context = null) {
        // Exit if the target step element is hidden
        const stepElement = this.querySelector(`[data-context="${context || this.currentStep.name}"]`);
        if (context && stepElement?.classList.contains('hide')) return;
      
        let offset = 20; // Base offset value
        if (this.elements.progressBar) {
          const progressBarStyle = window.getComputedStyle(this.elements.progressBar);
          const marginTop = parseFloat(progressBarStyle.marginTop) || 0;
          const marginBottom = parseFloat(progressBarStyle.marginBottom) || 0;
      
          // Calculate total offset from progress bar height and margins
          offset += this.elements.progressBar.offsetHeight + marginTop + marginBottom;
        }
      
        // Calculate scroll position
        if (stepElement) {
          const elementRect = stepElement.getBoundingClientRect();
          const elementPosition = elementRect.top + window.scrollY;
      
          // Scroll to the calculated position with smooth behavior
          window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
        }
      }

      /**
       * Moves the footer after the current step.
       */
      moveFooterAfterCurrentStep() {
        return;

        if (this.layout !== 'stacking-steps' || !this.elements.footer) return;
      
        const steps = this.elements.mainArea.querySelectorAll('[data-context]:not([data-context="footer"]):not([data-context="upsell"])');
      
        // Iterate over the steps in reverse order
        for (let i = steps.length - 1; i >= 0; i--) {
          const step = steps[i];
          if (!step.classList.contains('disabled') && step.childNodes.length > 0) {
            // Insert the footer after the current step
            const nextSibling = step.nextSibling;
            step.parentNode.insertBefore(this.elements.footer, nextSibling);
            break;
          }
        }
      }

      async handleDeoSamplerPromo() {
        await this.updateContent('offers', true);
        const deoSamplerOfferDiv = this.querySelector('[data-context="offers"] [data-deo-sampler]');

        if (deoSamplerOfferDiv) {
          // Update the URL with the offer details
          this.updateUrl(`${deoSamplerOfferDiv.getAttribute('data-handle')}:${deoSamplerOfferDiv.getAttribute('data-variant-id')}`, 'offers', 'add');
        } else {
          // Remove the 'offers' parameter from the URL
          this.updateUrl('', 'offers', 'remove');
        }
      }
    }
  );
}
